import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Table, Input, Select, Button, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import API_SERVICE from '../../shared/services/api-service';
import { INRFormatter } from '../../shared/services/Utility';
import { useSelector } from 'react-redux';
import { RootState } from 'shared/redux/store';

interface DataType {
  key: string;
  disbursalId: string;
  caseId: string;
  lenderIdentifier: string;
  lenderName: string;
  borrowerName: string;
  loanProductName: string;
  lenderDisbursementDate: string;
  disbursalDate: string;
  insuranceAmount: number;
  disbursedAmount: number;
  lenderDisbursementAmount: number;
  disbursementDifference: number;
  connectorPayoutComputed: number;
  connectorPayoutRevised: number;
  payoutDifference: number;
  salesUserHierarchy: string;
  comment?: string;
  status?: string;
}

interface Props {
  filterValues: any;
  onReconcileDataSequenceComplete?: () => void;
}

// Create component with forwardRef
const ReconciliationDiscrepancies = (props: Props, ref: React.Ref<unknown>) => {
  const { filterValues, onReconcileDataSequenceComplete } = props;
  
  // Set locale to English
  moment.locale('en');

  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [data, setData] = useState<DataType[]>([]);
  const { searchParam } = useSelector((state: RootState) => state.reconciliation);

  // Define a loadData function for reuse in API callbacks
  const loadData = React.useCallback(() => {
    setLoading(true);
    API_SERVICE.getReconcileDisbursedCases({
      pageNo,
      pageSize,
      status: 'RECONCILED_OPEN_DISCREPANCIES',
      searchParam,
      ...filterValues // Include filter values in the API call
    })
      .then((res: any) => {
        const content = (res?.data?.payload?.content || []).map((row: any) => ({
          ...row,
          key: row.disbursalId || row.key
        }));
        setData(content);
        setTotalCount(res?.data?.payload?.totalElements || 0);
      })
      .catch((error) => {
        notification.error({ message: "Failed to load discrepancy cases" });
      })
      .finally(() => {
        setLoading(false);
        onReconcileDataSequenceComplete?.();
      });
  }, [pageNo, pageSize, filterValues, searchParam, onReconcileDataSequenceComplete]);

  // Call loadData when component mounts or dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    loadData
  }));

  const handlePaginationChange = (page: number, size: number = pageSize) => {
    setPageNo(page);
    if (size !== pageSize) {
      setPageSize(size);
      setPageNo(1);
    }
  };

  const handleStatusChange = (value: string, record: DataType) => {
    const updatedData = data.map(item => {
      if (item.key === record.key) {
        return {
          ...item,
          status: value,
          comment: ''
        };
      }
      return item;
    });
    setData(updatedData);
  };

  const handleCommentChange = (comment: string, record: DataType) => {
    const updatedData = data.map(item => {
      if (item.key === record.key) {
        return {
          ...item,
          comment
        };
      }
      return item;
    });
    setData(updatedData);
  };

  // Save adjusted discrepancy
  const saveDiscrepancy = async (record: DataType) => {
    if (!record) return;
    
    // Validate: Comment is required when status is Adjusted
    if (record.status === 'Adjusted' && (!record.comment || record.comment.trim() === '')) {
      notification.error({ message: "Comment is required when marking as Adjusted" });
      return;
    }
    
    // Prepare payload as per the required structure
    const payload = {
      disbursalId: record.disbursalId,
      reconcileStatus: "RECONCILED_CLOSED_DISCREPANCIES",
      adjustDiscrepanciesComment: record.comment,
      // Include the connectorPayoutRevised value from the record
      connectorPayoutRevised: record.connectorPayoutRevised
    };

    // Send data to API
    setLoading(true);
    API_SERVICE.updateReconciliationDisbursal(payload)
      .then((res: any) => {
        // Refresh the data
        loadData();
        notification.success({ message: "Discrepancy closed successfully." });
      })
      .catch((error: any) => {
        notification.error({ message: "Failed to close discrepancy." });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns: ColumnsType<DataType> = [
    { title: 'Disbursal ID', dataIndex: 'disbursalId', key: 'disbursalId', fixed: 'left', render: (value: string) => value ?? '' },
    { title: 'Case ID', dataIndex: 'caseId', key: 'caseId', fixed: 'left', render: (value: string) => value ?? '' },
    { title: 'Lender Identifier', dataIndex: 'lenderIdentifier', key: 'lenderIdentifier', render: (value: string) => value ?? '' },
    { title: 'Lender', dataIndex: 'lenderName', key: 'lenderName', render: (value: string) => value ?? '' },
    { title: 'Customer Name', dataIndex: 'borrowerName', key: 'borrowerName', render: (value: string) => value ?? '' },
    { title: 'Product', dataIndex: 'loanProductName', key: 'loanProductName', render: (value: string) => value ?? '' },
    { title: 'Lender Disbursement Date', dataIndex: 'lenderDisbursementDate', key: 'lenderDisbursementDate',},
    { title: 'Disbursement Date', dataIndex: 'disbursalDate', key: 'disbursalDate',},
    { title: 'Insurance Amount', dataIndex: 'insuranceAmount', key: 'insuranceAmount', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Disbursement Amount', dataIndex: 'disbursedAmount', key: 'disbursedAmount', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Lender Disbursement Amount', dataIndex: 'lenderDisbursementAmount', key: 'lenderDisbursementAmount', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Disbursement Difference', dataIndex: 'disbursementDifference', key: 'disbursementDifference', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Connector Payout Computed', dataIndex: 'connectorPayoutComputed', key: 'connectorPayoutComputed', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Connector Payout Revised', dataIndex: 'connectorPayoutRevised', key: 'connectorPayoutRevised', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Payout Difference', dataIndex: 'payoutDifference', key: 'payoutDifference', align: 'right', render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '' },
    { title: 'Sales User Hierarchy', dataIndex: 'salesUserHierarchy', key: 'salesUserHierarchy', render: (value: string) => value ?? '' },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <div className="reconciliation-actions-container">
          <Select
            size="small"
            placeholder="Select status"
            value={record.status}
            style={{ width: 120 }}
            onChange={(value) => handleStatusChange(value, record)}
          >
            <Select.Option value="Adjusted">Adjusted</Select.Option>
          </Select>
          {record.status === 'Adjusted' && (
            <>
              <Input
                size="small"
                placeholder="Add Comments"
                value={record.comment}
                onChange={(e) => handleCommentChange(e.target.value, record)}
                maxLength={100}
                className="reconciliation-field"
                style={{ width: 110, marginLeft: 8 }}
              />
              <Button
                size="small"
                type="primary"
                onClick={() => saveDiscrepancy(record)}
                style={{ marginLeft: 8 }}
                disabled={!record.comment || record.comment.trim() === ''}
              >
                Save
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    preserveSelectedRowKeys: true,
  };

  return (
    <div className="reconciliation-content-box">
      <Table
        className="dynamic-pagination-checkbox table-full-width reconciliation-tight-table reconciliation-table-with-footer"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="disbursalId"
        scroll={{ x: 'max-content' }}
        size="small"
        pagination={{
          current: pageNo,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          position: ['bottomRight'],
          onChange: handlePaginationChange,
          onShowSizeChange: handlePaginationChange,
        }}
      />
    </div>
  );
};

// Use forwardRef to create the forwardable component
const ForwardedReconciliationDiscrepancies = forwardRef(ReconciliationDiscrepancies);

export default ForwardedReconciliationDiscrepancies;